// Iter81 — Option Chain Rows Editor.
//
// Reusable, client-side table editor that supplements the existing
// JSON mini-editor inside `FriendlyScannerForm` chain fields. The
// operator can build / edit an option-chain array row-by-row instead
// of hand-writing JSON.
//
// HARD CONSTRAINTS:
//  * No outbound HTTP. No `import axios`. No `fetch(`. No
//    `XMLHttpRequest`. No `navigator.sendBeacon`. No effect-hooks.
//  * No backend calls of any kind. The editor only reads / writes
//    the JSON string supplied by the host form.
//  * No fresh chain fetch, no broker / account lookup, no live
//    market-data fetch.
//  * No ranking. No "winner" / "best" / "top" / "preferred" /
//    "recommend" language.
//  * Output JSON shape is the EXACT shape the existing scanner
//    payloads expect:
//      [ { type, strike, bid, ask, delta, open_interest, volume,
//          expiration, dte } ]
//    The editor never invents fields the evaluator wouldn't read.
//
// The host form keeps its JSON mini-editor as the canonical fallback
// and source of truth — the row editor is rendered ABOVE the JSON
// textarea and round-trips through the same string state.

import React, { useState } from "react";
import { Plus, Trash2, Copy, AlertTriangle } from "lucide-react";

const ROW_FIELDS = [
  { key: "type",          label: "TYPE",    width: "w-14", input: "select",
    options: ["put", "call"] },
  { key: "strike",        label: "STRIKE",  width: "w-20", input: "number" },
  { key: "bid",           label: "BID",     width: "w-20", input: "number" },
  { key: "ask",           label: "ASK",     width: "w-20", input: "number" },
  { key: "delta",         label: "Δ",       width: "w-20", input: "number" },
  { key: "open_interest", label: "OI",      width: "w-20", input: "number" },
  { key: "volume",        label: "VOL",     width: "w-20", input: "number" },
  { key: "expiration",    label: "EXP",     width: "w-28", input: "date" },
  { key: "dte",           label: "DTE",     width: "w-16", input: "number" },
];

const EMPTY_ROW = Object.freeze({
  type: "put", strike: "", bid: "", ask: "", delta: "",
  open_interest: "", volume: "", expiration: "", dte: "",
});

// ── Pure helpers ──────────────────────────────────────────────────────

function parseRowsFromJson(text) {
  if (!text || !text.trim()) return [];
  try {
    const v = JSON.parse(text);
    if (!Array.isArray(v)) return [];
    return v.map(normalizeRow);
  } catch (_e) {
    return [];
  }
}

function normalizeRow(r) {
  const safe = (k) => (r && r[k] !== undefined && r[k] !== null) ? r[k] : "";
  return {
    type:          (r && (r.type === "call" || r.type === "put")) ? r.type : "put",
    strike:        safe("strike"),
    bid:           safe("bid"),
    ask:           safe("ask"),
    delta:         safe("delta"),
    open_interest: safe("open_interest"),
    volume:        safe("volume"),
    expiration:    safe("expiration"),
    dte:           safe("dte"),
  };
}

function rowsToJson(rows) {
  // Emit only the canonical scanner payload shape. Numeric fields
  // are converted with strict Number() — empty strings become null,
  // never invented placeholder values.
  const out = rows.map(r => {
    const obj = { type: r.type === "call" ? "call" : "put" };
    for (const key of ["strike", "bid", "ask", "delta",
                       "open_interest", "volume", "dte"]) {
      const s = r[key];
      if (s === "" || s === null || s === undefined) {
        obj[key] = null;
      } else {
        const n = Number(s);
        obj[key] = Number.isFinite(n) ? n : null;
      }
    }
    obj.expiration = r.expiration || null;
    return obj;
  });
  return JSON.stringify(out, null, 2);
}

function validateRow(r) {
  const warnings = [];
  if (r.strike === "" || r.strike === null) warnings.push("missing strike");
  if ((r.bid === "" || r.bid === null) && (r.ask === "" || r.ask === null)) {
    warnings.push("missing bid/ask");
  }
  if (!r.expiration) warnings.push("missing expiration");
  if (r.dte !== "" && r.dte !== null) {
    const n = Number(r.dte);
    if (!Number.isFinite(n) || n < 0) warnings.push("invalid DTE");
  }
  return warnings;
}

// ── Component ────────────────────────────────────────────────────────

export default function OptionChainRowsEditor({
  value, onChange, testid, kind,
}) {
  const [mode, setMode] = useState("table"); // "table" | "json"
  const rows = parseRowsFromJson(value);

  function commit(newRows) {
    onChange(rowsToJson(newRows));
  }

  function addRow() {
    const defaultType = kind === "calls" ? "call"
                      : kind === "puts"  ? "put"
                      : (rows[rows.length - 1]?.type || "put");
    commit([...rows, { ...EMPTY_ROW, type: defaultType }]);
  }

  function removeRow(idx) {
    commit(rows.filter((_r, i) => i !== idx));
  }

  function duplicateRow(idx) {
    const r = rows[idx];
    if (!r) return;
    const next = rows.slice();
    next.splice(idx + 1, 0, { ...r });
    commit(next);
  }

  function editRow(idx, key, newVal) {
    const next = rows.map((r, i) => i === idx ? { ...r, [key]: newVal } : r);
    commit(next);
  }

  return (
    <div
      data-testid={`${testid}-row-editor`}
      className="border border-[#26262B] rounded-sm bg-[#08080A] p-2 space-y-2"
    >
      {/* Mode toggle + Add buttons */}
      <div className="flex items-center gap-2 flex-wrap text-[9px] font-mono uppercase tracking-[0.14em]">
        <span className="text-[#5F5F66]">Chain</span>
        <button
          type="button"
          data-testid={`${testid}-row-editor-mode-table`}
          onClick={() => setMode("table")}
          className={`px-2 py-0.5 rounded-sm border ${mode === "table" ? "border-[#E0E0E0]/40 bg-[#E0E0E0]/10 text-[#E0E0E0]" : "border-[#26262B] text-[#5F5F66] hover:text-[#E0E0E0]"}`}
        >
          Table editor
        </button>
        <button
          type="button"
          data-testid={`${testid}-row-editor-mode-json`}
          onClick={() => setMode("json")}
          className={`px-2 py-0.5 rounded-sm border ${mode === "json" ? "border-[#E0E0E0]/40 bg-[#E0E0E0]/10 text-[#E0E0E0]" : "border-[#26262B] text-[#5F5F66] hover:text-[#E0E0E0]"}`}
        >
          Advanced JSON
        </button>
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            data-testid={`${testid}-row-editor-add-row`}
            onClick={addRow}
            className="px-2 py-0.5 rounded-sm border border-[#26262B] text-[#8A8A93] hover:text-[#E0E0E0] inline-flex items-center gap-1"
          >
            <Plus size={10} /> Add row
          </button>
          <button
            type="button"
            data-testid={`${testid}-row-editor-add-put`}
            onClick={() => commit([...rows, { ...EMPTY_ROW, type: "put" }])}
            className="px-2 py-0.5 rounded-sm border border-[#26262B] text-[#8A8A93] hover:text-[#E0E0E0]"
          >
            + Put
          </button>
          <button
            type="button"
            data-testid={`${testid}-row-editor-add-call`}
            onClick={() => commit([...rows, { ...EMPTY_ROW, type: "call" }])}
            className="px-2 py-0.5 rounded-sm border border-[#26262B] text-[#8A8A93] hover:text-[#E0E0E0]"
          >
            + Call
          </button>
          <button
            type="button"
            data-testid={`${testid}-row-editor-clear`}
            onClick={() => commit([])}
            className="px-2 py-0.5 rounded-sm border border-[#26262B] text-[#8A8A93] hover:text-[#E0E0E0]"
          >
            Clear
          </button>
        </div>
      </div>

      {mode === "table" && (
        <div
          data-testid={`${testid}-row-editor-table`}
          className="overflow-x-auto"
        >
          <table className="text-[10px] font-mono w-full">
            <thead>
              <tr className="text-[#5F5F66] uppercase tracking-[0.12em]">
                {ROW_FIELDS.map(f => (
                  <th key={f.key} className={`text-left pr-2 ${f.width}`}>
                    {f.label}
                  </th>
                ))}
                <th className="w-10"></th>
                <th className="w-10"></th>
                <th>WARN</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td
                    colSpan={ROW_FIELDS.length + 3}
                    data-testid={`${testid}-row-editor-empty`}
                    className="text-[#5F5F66] py-2 italic"
                  >
                    No rows yet. Click Add row, + Put, or + Call to begin.
                  </td>
                </tr>
              )}
              {rows.map((r, idx) => {
                const warnings = validateRow(r);
                return (
                  <tr
                    key={idx}
                    data-testid={`${testid}-row-editor-row-${idx}`}
                    className="border-t border-[#1A1A1F]"
                  >
                    {ROW_FIELDS.map(f => (
                      <td key={f.key} className="pr-2 py-0.5">
                        <RowCell
                          field={f}
                          value={r[f.key]}
                          onChange={v => editRow(idx, f.key, v)}
                          testid={`${testid}-row-editor-row-${idx}-${f.key}`}
                        />
                      </td>
                    ))}
                    <td className="py-0.5">
                      <button
                        type="button"
                        title="Duplicate row"
                        data-testid={`${testid}-row-editor-row-${idx}-duplicate`}
                        onClick={() => duplicateRow(idx)}
                        className="text-[#5F5F66] hover:text-[#E0E0E0] inline-flex items-center"
                      >
                        <Copy size={10} />
                      </button>
                    </td>
                    <td className="py-0.5">
                      <button
                        type="button"
                        title="Remove row"
                        data-testid={`${testid}-row-editor-row-${idx}-remove`}
                        onClick={() => removeRow(idx)}
                        className="text-[#FF3366] hover:text-[#FF6688] inline-flex items-center"
                      >
                        <Trash2 size={10} />
                      </button>
                    </td>
                    <td className="py-0.5">
                      {warnings.length > 0 ? (
                        <span
                          data-testid={`${testid}-row-editor-row-${idx}-warnings`}
                          className="inline-flex items-center gap-1 text-[#FFB800]"
                          title={warnings.join(", ")}
                        >
                          <AlertTriangle size={10} />
                          <span className="text-[9px] uppercase tracking-[0.12em]">
                            {warnings[0]}
                          </span>
                        </span>
                      ) : (
                        <span className="text-[#5F5F66] text-[9px]">ok</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {mode === "json" && (
        <textarea
          data-testid={`${testid}-row-editor-json`}
          value={value || ""}
          onChange={e => onChange(e.target.value)}
          rows={4}
          spellCheck={false}
          placeholder='[ { "type":"put", "strike":490, "bid":1.6, "ask":1.65, "delta":-0.25, "open_interest":1500, "volume":100, "expiration":"2026-06-21", "dte":21 } ]'
          className="w-full bg-[#08080A] border border-[#26262B] rounded-sm px-1 py-0.5 text-[10px] font-mono text-[#E0E0E0] focus:outline-none"
        />
      )}
    </div>
  );
}

function RowCell({ field, value, onChange, testid }) {
  if (field.input === "select") {
    return (
      <select
        data-testid={testid}
        value={value || "put"}
        onChange={e => onChange(e.target.value)}
        className={`bg-[#08080A] border border-[#26262B] rounded-sm px-1 py-0.5 text-[#E0E0E0] ${field.width}`}
      >
        {field.options.map(opt => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
    );
  }
  if (field.input === "date") {
    return (
      <input
        type="text"
        placeholder="YYYY-MM-DD"
        data-testid={testid}
        value={value || ""}
        onChange={e => onChange(e.target.value)}
        className={`bg-[#08080A] border border-[#26262B] rounded-sm px-1 py-0.5 text-[#E0E0E0] ${field.width}`}
      />
    );
  }
  return (
    <input
      type="text"
      data-testid={testid}
      value={value === null || value === undefined ? "" : value}
      onChange={e => onChange(e.target.value)}
      className={`bg-[#08080A] border border-[#26262B] rounded-sm px-1 py-0.5 text-[#E0E0E0] ${field.width}`}
    />
  );
}
